// FAVORITES (2026-10-30). One door on the shelf, on every surface, to what the reader has pinned:
// their FEATURES (the tools they pinned from a tool page, the same pins that lead the sidebar and
// fill the phone dock) and their STORIES (the cards they saved with the bookmark). Two groups,
// never mixed, so a story never looks like a tool and the list never looks crowded. Everything
// stays in this browser (localStorage); nothing leaves the device.
//
// The reader owns its saved ids as state (it writes insnip.saved), so it passes them in with its
// own unsave; the home reads the same key straight from storage. Story details come from the
// worker by id, a handful at a time, and are remembered for the session.
//
// The panel and its scrim are both drawn on the body, never inside the shelf: the shelves are
// glass (backdrop-filter), which makes them a stacking context, so a panel left inside one sits
// UNDER a body-level scrim no matter its z-index and every click on a favorite only closed the
// panel (USER 2026-10-30: "Favorites should go to that page or card"). On a desktop the panel is
// fixed just under the pill, measured from the pill; on a phone it is the bottom sheet.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Bookmark, Pin, X } from "lucide-react";
import type { InSnipCard } from "./types";
import { fetchCard } from "./sessionApi";
import { TOOLS, readFigures, type ToolId } from "./more/tools";
import { readFavs, toggleFav } from "./more/favs";
import { sourceName } from "./v3/editionTime";

export const SAVED_KEY = "insnip.saved";
const CACHE_KEY = "insnip.favcards";
const STORIES_SHOWN = 12;
const PHONE = "(max-width: 760px)";
const MENU_GAP = 8;
const MENU_INSET = 12;
/** The full cards fetched for the panel, so a story can open in place without a second fetch. */
const FULL = new Map<string, InSnipCard>();
/** Stories the worker could not give us this session (offline, or gone): shown as such and asked for again next time, never cached as lost. */
const MISSING = new Set<string>();

/** Where the desktop panel sits: under the pill, flush with its right edge, never off screen. */
export function menuPlace(pill: { bottom: number; right: number }, innerWidth: number): { top: number; right: number } {
  return { top: Math.round(pill.bottom + MENU_GAP), right: Math.max(MENU_INSET, Math.round(innerWidth - pill.right)) };
}

export function readSavedIds(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(SAVED_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function writeSavedIds(ids: string[]) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(ids)); } catch { /* private mode */ }
  window.dispatchEvent(new Event("insnip:saved"));
}

type Story = { id: string; headline: string; source: string; url?: string; at?: string; category?: string };
function readCache(): Record<string, Story> {
  try {
    const raw = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}");
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}
function toStory(c: InSnipCard): Story {
  return { id: c.id, headline: c.headline, source: c.primarySourceName || "", url: c.primarySourceUrl, at: c.publishedAt, category: c.category };
}
function sourceLabel(raw: string, url?: string): string {
  return sourceName(raw, url);
}
function agoWord(iso?: string): string {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return "";
  const h = Math.round((Date.now() - t) / 36e5);
  if (h < 1) return "just now";
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d} days ago`;
}

export function Favorites({
  savedIds,
  cards,
  onUnsave,
  onOpenStory,
  onOpenTool,
  compact = false,
}: {
  /** The reader's own saved ids; left out, the key is read from storage. */
  savedIds?: string[];
  /** Cards already in hand (the round), so a saved story need not be fetched. */
  cards?: InSnipCard[];
  onUnsave?: (id: string) => void;
  /** Open a story in place, with the full card when the panel has it; left out, the link goes to the reader. */
  onOpenStory?: (id: string, card?: InSnipCard) => void;
  /** Open a tool in place; left out, the link goes to its page. */
  onOpenTool?: (path: string) => void;
  /** Icon only (a phone shelf). */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [favs, setFavs] = useState<ToolId[]>(readFavs);
  const [storedIds, setStoredIds] = useState<string[]>(readSavedIds);
  const [stories, setStories] = useState<Record<string, Story>>(readCache);
  const [place, setPlace] = useState<CSSProperties | undefined>(undefined);
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const ids = savedIds ?? storedIds;

  // Measure the pill before the panel is shown, so it never draws anywhere else first.
  const fit = useCallback(() => {
    const phone = typeof window.matchMedia === "function" && window.matchMedia(PHONE).matches;
    const r = pillRef.current?.getBoundingClientRect();
    setPlace(!phone && r ? menuPlace(r, window.innerWidth) : undefined);
  }, []);
  const show = useCallback(() => { fit(); setOpen(true); }, [fit]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", fit);
    window.addEventListener("scroll", fit, true);
    return () => { window.removeEventListener("resize", fit); window.removeEventListener("scroll", fit, true); };
  }, [open, fit]);

  useEffect(() => {
    const sync = () => { setFavs(readFavs()); setStoredIds(readSavedIds()); };
    window.addEventListener("insnip:favs", sync);
    window.addEventListener("insnip:saved", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("insnip:favs", sync); window.removeEventListener("insnip:saved", sync); window.removeEventListener("storage", sync); };
  }, []);

  // The stories' details, when the panel opens: from the cards in hand first, then the worker.
  useEffect(() => {
    if (!open) return;
    const want = [...ids].reverse().slice(0, STORIES_SHOWN);
    const next: Record<string, Story> = { ...stories };
    let changed = false;
    for (const id of want) {
      if (next[id]) continue;
      const c = cards?.find((x) => x.id === id);
      if (c) { next[id] = toStory(c); FULL.set(id, c); changed = true; }
    }
    if (changed) setStories(next);
    const missing = want.filter((id) => !next[id]);
    if (!missing.length) return;
    let alive = true;
    void Promise.all(missing.map((id) => fetchCard(id).catch(() => null))).then((got) => {
      if (!alive) return;
      setStories((cur) => {
        const out = { ...cur };
        // A failed fetch is not a lost story: it stays unfilled, says so, and is asked for again next time.
        got.forEach((c, i) => { if (c) { out[missing[i]] = toStory(c); FULL.set(missing[i], c); MISSING.delete(missing[i]); } else MISSING.add(missing[i]); });
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(out)); } catch { /* full or private */ }
        return out;
      });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ids.join(",")]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const unsave = useCallback((id: string) => {
    if (onUnsave) onUnsave(id);
    else writeSavedIds(readSavedIds().filter((x) => x !== id));
  }, [onUnsave]);

  const figures = open ? readFigures() : {};
  const pinned = favs.map((id) => TOOLS.find((t) => t.id === id)).filter((t): t is (typeof TOOLS)[number] => !!t);
  const shownStories = [...ids].reverse().slice(0, STORIES_SHOWN);
  const count = pinned.length + ids.length;

  const panel: ReactNode = open ? createPortal(
    <>
      <button type="button" className="fav-scrim" aria-label="Close favorites" onClick={() => setOpen(false)} />
      <div className="fav-menu" role="dialog" aria-label="Favourites" style={place}>
        <div className="fav-head">
          <b>Favourites</b>
          <button type="button" className="fav-close" onClick={() => setOpen(false)} aria-label="Close"><X size={15} /></button>
        </div>

        <p className="fav-label">Tools</p>
        {pinned.length ? (
          <ul className="fav-list">
            {pinned.map((t) => {
              const Icon = t.icon;
              const fig = figures[t.id];
              return (
                <li key={t.id}>
                  <a href={t.path} onClick={(e) => { if (onOpenTool && !e.metaKey && !e.ctrlKey) { e.preventDefault(); setOpen(false); onOpenTool(t.path); } }}>
                    <Icon size={15} aria-hidden="true" />
                    <span className="fav-title">{t.title}</span>
                    {fig?.figure ? <span className="fav-fig">{fig.figure}</span> : null}
                  </a>
                  <button type="button" className="fav-x" onClick={() => { toggleFav(t.id); setFavs(readFavs()); }} aria-label={`Unpin ${t.title}`} title="Unpin"><X size={13} /></button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="fav-empty">Nothing pinned yet. Open a tool and press Pin, and it lives here.</p>
        )}

        <p className="fav-label">Stories</p>
        {shownStories.length ? (
          <ul className="fav-list">
            {shownStories.map((id) => {
              const s = stories[id];
              return (
                <li key={id}>
                  <a href={`/read#card=${encodeURIComponent(id)}&via=favorites`} onClick={(e) => { if (onOpenStory && !e.metaKey && !e.ctrlKey) { e.preventDefault(); setOpen(false); onOpenStory(id, cards?.find((x) => x.id === id) ?? FULL.get(id)); } }}>
                    <Bookmark size={15} aria-hidden="true" />
                    <span className="fav-title">{s ? s.headline : MISSING.has(id) ? "Could not load this story yet" : "Fetching the story"}</span>
                    {s && (s.source || s.at) ? <span className="fav-meta">{[sourceLabel(s.source, s.url), agoWord(s.at)].filter(Boolean).join(", ")}</span> : null}
                  </a>
                  <button type="button" className="fav-x" onClick={() => unsave(id)} aria-label="Remove from favorites" title="Remove"><X size={13} /></button>
                </li>
              );
            })}
            {ids.length > STORIES_SHOWN ? <li className="fav-more"><a href="/read#saved">All {ids.length} saved stories</a></li> : null}
          </ul>
        ) : (
          <p className="fav-empty">No stories saved yet. The bookmark on any card keeps it here.</p>
        )}
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <div className={`fav-wrap${open ? " open" : ""}`}>
      <button
        type="button"
        ref={pillRef}
        className={`fav-trigger${count ? " has" : ""}${compact ? " compact" : ""}`}
        onClick={() => (open ? setOpen(false) : show())}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={count ? `Favourites, ${count}` : "Favourites"}
        title="Favourites: your pinned tools and saved stories"
      >
        <Pin size={15} aria-hidden="true" />
        <span className="fav-trigger-label">Favourites</span>
        {count ? <i className="fav-count" aria-hidden="true">{count > 99 ? "99+" : count}</i> : null}
      </button>
      {panel}
    </div>
  );
}
